// Procedurelyd med WebAudio: ingen filer (DESIGN.md: "Lyd"). Alt går gennem Mixeren, som låser
// lyden op ved første tryk, holder busser og stjæler stemmer. Lydene er korte og bløde: legetøj,
// ikke alarm. Navnene følger signaturtabellen i DESIGN.md.

import type { Mixer } from './mixer';

export type SfxKind =
  | 'pop'
  | 'ding'
  | 'rumble'
  | 'blackout'
  | 'tap'
  | 'error'
  | 'coin'
  | 'win'
  | 'warn'
  | 'build'
  | 'snap'
  | 'stretch'
  | 'upgrade'
  | 'chord'
  | 'crack'
  | 'crew'
  | 'crewDone'
  | 'thunder'
  | 'horn'
  | 'tick'
  | 'fanfare'
  | 'lose'
  | 'reinforce'
  | 'demolish'
  | 'whoosh'
  | 'lesson'
  | 'relit'
  | 'hum'
  | 'beep'
  | 'flip'
  | 'zap'
  | 'powerDown'
  | 'norway'
  | 'shimmer'
  | 'chime'
  | 'clink'
  | 'phew'
  | 'lyn';

/** C-dur pentatonisk over to oktaver: C D E G A. Alle toner klinger pænt sammen. */
export const PENTA = [261.63, 293.66, 329.63, 392.0, 440.0, 523.25, 587.33, 659.25, 783.99, 880.0, 1046.5, 1174.66, 1318.51];

export function penta(i: number): number {
  const n = PENTA.length;
  const oct = Math.floor(i / n);
  return (PENTA[((i % n) + n) % n] ?? 440) * 2 ** oct;
}

const GAP_MS: Partial<Record<SfxKind, number>> = { clink: 1000, stretch: 40, tick: 90, crack: 120, chord: 90, thunder: 900, hum: 400, zap: 300, norway: 600, shimmer: 500 };

export class Sfx {
  private last = new Map<SfxKind, number>();

  constructor(private mix: Mixer) {}

  get muted(): boolean {
    return this.mix.isMuted;
  }

  setMuted(m: boolean): void {
    this.mix.setMuted(m);
  }

  /** Spil en lyd. `arg` er lydens eget tal: combo for akkorden, segment for træk, fart for tik. */
  play(kind: SfxKind, arg = 0): void {
    const m = this.mix;
    if (!m.live) return;
    // Samme lyd højst hvert 60. ms, så et regn af hændelser ikke larmer.
    const now = performance.now();
    if (now - (this.last.get(kind) ?? -1e9) < (GAP_MS[kind] ?? 60)) return;
    this.last.set(kind, now);
    const t = m.now + 0.005;
    switch (kind) {
      case 'tap':
        m.tone('ui', t, 'sine', 700, 760, 0.05, 0.1);
        break;
      case 'pop':
        m.tone('ui', t, 'sine', 520, 880, 0.09, 0.22);
        m.tone('ui', t + 0.04, 'triangle', 980, 1320, 0.07, 0.1);
        break;
      case 'error':
        m.tone('ui', t, 'triangle', 300, 220, 0.16, 0.14);
        m.tone('ui', t + 0.1, 'triangle', 240, 180, 0.14, 0.1);
        break;
      case 'stretch': {
        // Elastikken: én pentatonisk tone pr. segment, stigende.
        const f = penta(Math.min(12, arg) + 3);
        m.tone('sfx', t, 'triangle', f, f, 0.08, 0.07);
        break;
      }
      case 'snap':
        // Klik, når ledningen griber fat i målet.
        m.noise('sfx', t, 0.03, 3200, 0.2, 'bandpass', 0.002, 2);
        m.tone('sfx', t, 'square', 1800, 1200, 0.03, 0.05, 0.002);
        break;
      case 'build':
        // Ledningen klikker fast: et blødt klonk og en lys tone ovenpå.
        m.tone('sfx', t, 'sine', 180, 120, 0.14, 0.3, 0.004);
        m.noise('sfx', t, 0.05, 2400, 0.12, 'bandpass', 0.002, 1.5);
        m.tone('sfx', t + 0.05, 'triangle', 784, 1046, 0.12, 0.1);
        break;
      case 'upgrade':
        // Opadgående "fwip" og et dybt "dunk".
        m.noise('sfx', t, 0.16, 900, 0.14, 'bandpass', 0.06, 3);
        m.tone('sfx', t, 'sine', 300, 1200, 0.14, 0.09, 0.05);
        m.tone('sfx', t + 0.15, 'sine', 110, 55, 0.28, 0.42, 0.004);
        m.tone('sfx', t + 0.15, 'triangle', 220, 140, 0.12, 0.12, 0.004);
        break;
      case 'chord': {
        // En by tænder: lille akkord, der stiger med combo.
        const root = Math.min(6, arg) * 2;
        [0, 2, 4].forEach((step, i) => {
          const f = penta(root + step + 5);
          m.tone('sfx', t + i * 0.045, 'sine', f, f, 0.6, 0.12 - i * 0.02);
          m.tone('sfx', t + i * 0.045, 'triangle', f * 2, f * 2, 0.2, 0.025);
        });
        break;
      }
      case 'ding':
        m.tone('sfx', t, 'sine', 1046.5, 1046.5, 0.6, 0.16);
        m.tone('sfx', t + 0.07, 'sine', 1568, 1568, 0.5, 0.1);
        break;
      case 'relit':
        m.tone('sfx', t, 'sine', 523, 523, 0.25, 0.12);
        m.tone('sfx', t + 0.09, 'sine', 784, 784, 0.4, 0.12);
        break;
      case 'coin':
        m.tone('ui', t, 'square', 988, 988, 0.06, 0.04);
        m.tone('ui', t + 0.06, 'square', 1319, 1319, 0.14, 0.04);
        break;
      case 'warn':
        m.tone('sfx', t, 'triangle', 660, 660, 0.12, 0.12);
        m.tone('sfx', t + 0.16, 'triangle', 660, 660, 0.12, 0.12);
        break;
      case 'crack':
        // Brud: blødt "tsjak" og gnistknitren.
        m.noise('sfx', t, 0.12, 1800, 0.3, 'bandpass', 0.002, 1.2);
        m.tone('sfx', t, 'triangle', 420, 140, 0.16, 0.14, 0.003);
        for (let i = 0; i < 6; i++) m.noise('sfx', t + 0.08 + i * 0.045 + (i % 2) * 0.02, 0.02, 5000 + i * 400, 0.08, 'highpass', 0.001);
        m.duck(0.5, 400);
        break;
      case 'crew':
        // Montørbilen: "bip bip".
        m.tone('sfx', t, 'square', 880, 880, 0.07, 0.04, 0.003);
        m.tone('sfx', t + 0.13, 'square', 880, 880, 0.07, 0.04, 0.003);
        break;
      case 'crewDone':
        // "Dink dink dink" og et pling.
        [0, 0.11, 0.22].forEach((d) => m.tone('sfx', t + d, 'triangle', 1568, 1568, 0.06, 0.08, 0.002));
        m.tone('sfx', t + 0.36, 'sine', 2093, 2093, 0.5, 0.1);
        break;
      case 'reinforce':
        // Masten strammes: to korte dunk og en lys pling.
        m.tone('sfx', t, 'sine', 160, 110, 0.1, 0.25, 0.003);
        m.tone('sfx', t + 0.1, 'sine', 180, 120, 0.1, 0.25, 0.003);
        m.tone('sfx', t + 0.2, 'triangle', 1318, 1318, 0.25, 0.07);
        break;
      case 'demolish':
        m.tone('sfx', t, 'triangle', 500, 160, 0.2, 0.12);
        m.noise('sfx', t, 0.2, 700, 0.12);
        break;
      case 'rumble':
      case 'thunder':
        // Tordenbuldren: lav støj, der ruller ind og ud.
        m.noise('sfx', t, 1.6, 160, 0.4, 'lowpass', 0.25);
        m.noise('sfx', t + 0.1, 0.5, 600, 0.14, 'lowpass', 0.02);
        m.tone('sfx', t, 'sine', 55, 38, 1.3, 0.22, 0.1);
        m.duck(0.55, 900);
        break;
      case 'horn':
        // Skibet: to tågetud.
        m.tone('sfx', t, 'sawtooth', 110, 108, 0.5, 0.06, 0.08);
        m.tone('sfx', t + 0.7, 'sawtooth', 110, 106, 0.6, 0.06, 0.08);
        break;
      case 'tick': {
        // Mørkeur: tik, der bliver lysere og hurtigere.
        const f = 1400 + Math.min(1, arg) * 600;
        m.tone('sfx', t, 'square', f, f, 0.025, 0.03, 0.001);
        break;
      }
      case 'hum':
        // Overbelastning: lav brummen.
        m.tone('sfx', t, 'sawtooth', 70 + arg * 40, 80 + arg * 50, 0.45, 0.035, 0.1);
        break;
      case 'blackout':
        // "Pwoomp" og et lille kollektivt suk.
        m.tone('sfx', t, 'sine', 300, 50, 0.55, 0.4, 0.005);
        m.noise('sfx', t, 0.3, 300, 0.2, 'lowpass', 0.005);
        m.tone('sfx', t + 0.35, 'triangle', 520, 330, 0.7, 0.06, 0.2);
        m.tone('sfx', t + 0.4, 'triangle', 440, 280, 0.7, 0.05, 0.2);
        m.duck(0.35, 1200);
        break;
      case 'fanfare':
        // Årsskifte: lille fanfare.
        [523.25, 659.25, 783.99].forEach((f, i) => m.tone('sfx', t + i * 0.09, 'triangle', f, f, 0.18, 0.1));
        m.tone('sfx', t + 0.3, 'triangle', 1046.5, 1046.5, 0.6, 0.13);
        m.tone('sfx', t + 0.3, 'sine', 523.25, 523.25, 0.6, 0.1);
        m.duck(0.5, 900);
        break;
      case 'win':
        [523, 659, 784, 1046, 1318].forEach((f, i) => m.tone('sfx', t + i * 0.1, 'sine', f, f, 0.5, 0.14));
        [523, 659, 784].forEach((f) => m.tone('sfx', t + 0.55, 'triangle', f, f, 1.6, 0.07, 0.05));
        m.duck(0.4, 1800);
        break;
      case 'lose':
        [392, 349, 311, 262].forEach((f, i) => m.tone('sfx', t + i * 0.22, 'triangle', f, f * 0.98, 0.35, 0.1));
        m.duck(0.4, 1500);
        break;
      case 'whoosh':
        m.noise('ui', t, 0.25, 1200, 0.08, 'bandpass', 0.1, 1.5);
        break;
      case 'beep':
        // Gravkoen bakker: "biip biip biip".
        [0, 0.18, 0.36].forEach((d) => m.tone('sfx', t + d, 'square', 1046, 1046, 0.09, 0.035, 0.003));
        break;
      case 'flip':
        // Et kort vendes: papirsvup og et lille klik.
        m.noise('ui', t, 0.08, 2600, 0.1, 'bandpass', 0.01, 1.2);
        m.tone('ui', t + 0.05, 'triangle', 660, 990, 0.08, 0.09);
        break;
      case 'chime':
        // Et svar er valgt: to lyse toner, der lander.
        m.tone('sfx', t, 'sine', 784, 784, 0.18, 0.11);
        m.tone('sfx', t + 0.08, 'sine', 1174.66, 1174.66, 0.35, 0.1);
        break;
      case 'zap':
        // Lynreserve: et opladet "bzzzt-ping".
        m.noise('sfx', t, 0.12, 4000, 0.14, 'bandpass', 0.003, 4);
        m.tone('sfx', t, 'sawtooth', 220, 1760, 0.14, 0.05, 0.004);
        m.tone('sfx', t + 0.12, 'sine', 1568, 1568, 0.4, 0.12);
        m.tone('sfx', t + 0.12, 'triangle', 2093, 2093, 0.3, 0.05);
        break;
      case 'powerDown':
        // Nødaflastning: en lille sluk-lyd, venlig og ikke uhyggelig.
        m.tone('sfx', t, 'triangle', 660, 220, 0.35, 0.12, 0.005);
        m.tone('sfx', t + 0.05, 'sine', 330, 110, 0.4, 0.1, 0.005);
        break;
      case 'norway':
        // Ring til Norge: vandfaldsbrus og en stigende tone fra fjeldet.
        m.noise('sfx', t, 1.0, 700, 0.14, 'lowpass', 0.2);
        [0, 2, 4, 7].forEach((step, i) => m.tone('sfx', t + 0.1 + i * 0.09, 'sine', penta(step + 3), penta(step + 3), 0.4, 0.09));
        break;
      case 'shimmer':
        // Hedebølge: varm luft, der dirrer.
        m.tone('sfx', t, 'sine', 880, 932, 0.9, 0.05, 0.3);
        m.tone('sfx', t + 0.05, 'sine', 1318, 1244, 0.9, 0.035, 0.3);
        break;
      case 'clink':
        // Løbende indtægt: et blødt, lille klik fra kassen. Højst én gang i sekundet.
        m.tone('ui', t, 'sine', 1568, 1480, 0.05, 0.035, 0.002);
        m.tone('ui', t + 0.035, 'sine', 2093, 2093, 0.08, 0.02, 0.002);
        break;
      case 'phew':
        // Reddet i sidste øjeblik: et lettet suk, der falder til ro i en lys tone.
        m.noise('sfx', t, 0.35, 900, 0.07, 'bandpass', 0.08, 0.8);
        m.tone('sfx', t, 'sine', 660, 440, 0.32, 0.09, 0.03);
        m.tone('sfx', t + 0.26, 'sine', 784, 784, 0.45, 0.09);
        break;
      case 'lyn':
        // LYN-tilslutning: en hurtig opadgående trille.
        [0, 2, 4, 7].forEach((step, i) => m.tone('sfx', t + i * 0.04, 'triangle', penta(step + 5), penta(step + 5), 0.12, 0.07));
        break;
      case 'lesson':
        // Hånden er tilfreds: et lille pling op.
        m.tone('ui', t, 'sine', 1046.5, 1046.5, 0.15, 0.1);
        m.tone('ui', t + 0.08, 'sine', 1568, 1568, 0.35, 0.1);
        break;
    }
  }
}
